import React, { useEffect, useRef, useState } from 'react';

export function showToast(title, detail = '', level = 'info') {
  const event = new CustomEvent('notify', { detail: { title, detail, level } });
  window.dispatchEvent(event);
}

export default function NotificationTray() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const trayRef = useRef(null);

  useEffect(() => {
    function onNotify(e) {
      const { title, detail, level } = e.detail || {};
      const id = Date.now() + Math.random();
      // Add item but keep tray closed until user toggles it
      setItems((prev) => [{ id, title, detail, level }, ...prev].slice(0, 6));
    }
    window.addEventListener('notify', onNotify);
    return () => { window.removeEventListener('notify', onNotify); };
  }, []);

  // Click-away + Escape to close
  useEffect(() => {
    function handleDocInteract(e) {
      if (!open) return;
      const root = trayRef.current;
      if (!root) return;
      if (!root.contains(e.target)) {
        setOpen(false);
      }
    }
    function handleKey(e) {
      if (!open) return;
      if (e.key === 'Escape') setOpen(false);
    }

    document.addEventListener('mousedown', handleDocInteract);
    document.addEventListener('touchstart', handleDocInteract, { passive: true });
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleDocInteract);
      document.removeEventListener('touchstart', handleDocInteract);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  function remove(id) {
    setItems((prev) => prev.filter((x) => x.id !== id));
  }

  return (
    <div ref={trayRef} className="notification-tray" data-notification-tray>
      <button
        type="button"
        className="notification-pill"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
        title="Notifications"
      >
        <span className="notification-pill-icon" aria-hidden>🔔</span>
        <span className="notification-pill-label sr-only">Notifications</span>
        <span className="notification-pill-count" aria-hidden>{items.length}</span>
      </button>
      {open && (
        <div className="notification-list" role="region" aria-label="Notifications">
          {items.length === 0 && (
            <div className="toast toast-info" role="status">
              <div className="toast-icon">ℹ️</div>
              <div>
                <div className="toast-title">No notifications</div>
                <div className="toast-detail">You are all caught up.</div>
              </div>
              <div />
            </div>
          )}
          {items.map((n) => (
            <div key={n.id} className={`toast toast-${n.level}`} role="status">
              <div className="toast-icon" aria-hidden>✓</div>
              <div>
                <div className="toast-title">{n.title}</div>
                {n.detail && <div className="toast-detail">{n.detail}</div>}
              </div>
              <button className="toast-close" onClick={() => remove(n.id)} aria-label="Dismiss">✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
